import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';

import { ClickAwayListener, Grow, Paper, Popper, MenuItem, MenuList } from '@mui/material';

import ManageButton from '@/views/components/ManageButton/ManageButton';
import { CheckPermission } from '@/utils/permissions/permissions';

import styles from '@/views/pages/Departments/Departments.module.scss';

const DisabledOption = ({
	tooltipText,
	label,
}) => {
	const [ randomId ] = useState(String(Math.random()));

	return (<>
		<div
			data-tooltip-id={ `menu-item-tooltip-${randomId}` } 
			data-tooltip-html={ tooltipText }
			data-tooltip-place="top"
			data-html="true"
		>
			<MenuItem
				className='table__menu-item'
				disabled
			>
				{label}
			</MenuItem>
		</div>
		{createPortal(
			<Tooltip 
				id={ `menu-item-tooltip-${randomId}` } 
				className={ styles["tooltip-container"] }
			/>, 
			document.body,
		)}</>
	);
};

const DepartmentsItemMenu = ({
	onEditItem,
	setShowModalDeleteItem,
	setShowModalAddDepartment, 
	setShowModalHeaderFooter,
	onDeleteItem = null,
	data,
}) => {
	const { dict } = useSelector((state) => state.app);
	const { user: { role: userRole } } = useSelector((state) => state.auth);
	const [ open, setOpen ] = useState(false);
	const anchorRef = useRef(null);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (
			anchorRef.current &&
            anchorRef.current.contains(event.target)
		) {
			return;
		}
		setOpen(false);
	};

	const handleEdit = (ev) => {
		setShowModalAddDepartment(true);
		onEditItem(data);
		handleClose(ev);
	};

	const handleModalHeader = (ev) => {
		setShowModalHeaderFooter({show: true, header: true});
		onEditItem(data);
		handleClose(ev);
	};

	const handleModalFooter = (ev) =>{
		setShowModalHeaderFooter({show: true, header: false});
		onEditItem(data);
		handleClose(ev);
	};

	const handleDelete = (ev) => {
		setShowModalDeleteItem(true);
		onDeleteItem(data);
		handleClose(ev);
	};

	const handleListKeyDown = (event) => {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = useRef(open);
	useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current.focus();
		}
		prevOpen.current = open;
	}, [ open ]);

	const { canEditDepartmentHeaderAndFooter, canEdit, canDelete } = data.rights;

	return (
		<>
			<ManageButton 
				forwardRef={ anchorRef } 
				open={ open }
				handleToggle={ handleToggle }
				disabled={ !canEdit && !canEditDepartmentHeaderAndFooter && !canDelete }
			/>
			<Popper
				open={ open }
				anchorEl={ anchorRef.current }
				role={ undefined }
				placement='bottom-start'
				transition
				disablePortal
				className='mui__popper'
			>

				{({ TransitionProps, placement }) => (
					<Grow
						{ ...TransitionProps }
						style={ {
							transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
						} }
					>
						<Paper>
							<ClickAwayListener onClickAway={ handleClose }>
								<MenuList
									autoFocusItem={ open }
									id='composition-menu'
									aria-labelledby='composition-button'
									onKeyDown={ handleListKeyDown }
								>
									{canEdit && (
										<MenuItem
											className='table__menu-item'
											onClick={ handleEdit }
										>
											{dict.departments.list.edit}
										</MenuItem>
									)}
									{canEditDepartmentHeaderAndFooter && 
										<MenuItem
											className='table__menu-item'
											onClick={ handleModalHeader }
										>
											{dict.departments.list.editHeader}
										</MenuItem>
									}
									{canEditDepartmentHeaderAndFooter && <MenuItem
										className='table__menu-item'
										onClick={ handleModalFooter }
									>
										{dict.departments.list.editFooter}
									</MenuItem>
									}
									{CheckPermission("canDeleteDepartments", userRole) && <>
										{!canDelete && (
											<DisabledOption 
												tooltipText={ dict.departments.list.deleteTooltip }
												label={ dict.departments.list.delete }
											/>
										)}
										{canDelete && (
											<MenuItem
												className='table__menu-item'
												onClick={ handleDelete }
											>
												{dict.departments.list.delete}
											</MenuItem>
										)}
									</>}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

export default React.memo(DepartmentsItemMenu);